"use client";

import Image from "next/image";
import { Rock_Salt, Space_Grotesk } from "next/font/google";
import AppImage from "@/components/ui/AppImage";

const travisFont = Rock_Salt({
  subsets: ["latin"],
  weight: "400",
});

const infoFont = Space_Grotesk({
  subsets: ["latin"],
  weight: ["500", "700"],
});

const SAME_VIBE_DESKTOP_IMAGE = "/styleai/same-vibe-hero-desktop.png";
const SAME_VIBE_MOBILE_IMAGE = "/styleai/same-vibe-hero-mobile.png";

const budgetItems = [
  {
    category: "Top",
    name: "Oversized Graphic Tee",
    brand: "H&M",
    price: "$35",
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_119f36eda-1772686834701.png",
  },
  {
    category: "Bottom",
    name: "Baggy Work Pant",
    brand: "Dickies",
    price: "$50",
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_1213496dc-1772223729687.png",
  },
];

const premiumItems = [
  {
    category: "Top",
    name: "Boxy Heavyweight Tee",
    brand: "Fear of God",
    price: "$95",
    img: "https://images.unsplash.com/photo-1696185303064-2f8fde5e25c7",
  },
  {
    category: "Bottom",
    name: "Utility Cargo Pant",
    brand: "Stüssy",
    price: "$150",
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_1ab9cc3d4-1772275233904.png",
  },
];

export default function VibeComparisonSection() {
  return (
    <section className="relative overflow-hidden bg-black px-4 py-12 text-white">
      <div className="absolute inset-0 bg-black" />
      <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-black via-black to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-black via-black to-transparent" />

      <div className="relative mx-auto max-w-[1240px] px-4 pb-8 pt-2">
        <div className="relative z-30 overflow-hidden rounded-[28px] bg-black">
          <AppImage
            src={SAME_VIBE_DESKTOP_IMAGE}
            alt="FHITZ same vibe different price desktop hero"
            width={1672}
            height={941}
            className="hidden h-auto w-full object-cover md:block"
            priority
          />

          <AppImage
            src={SAME_VIBE_MOBILE_IMAGE}
            alt="FHITZ same vibe different price mobile hero"
            width={900}
            height={1400}
            className="block h-auto w-full object-cover md:hidden"
            priority
          />
        </div>

        <div className="relative z-40 mt-[-75px] grid items-start gap-5 md:grid-cols-[1fr_160px_1fr]">
          <BuildCard
            type="budget"
            title="Budget Build"
            tag="Under $90"
            total="$85"
            items={budgetItems}
          />

          <SameEnergy />

          <BuildCard
            type="premium"
            title="Premium Build"
            tag="Under $250"
            total="$245"
            items={premiumItems}
          />
        </div>

        <BottomBar />
      </div>
    </section>
  );
}

function SameEnergy() {
  return (
    <div className="relative hidden min-h-[285px] items-start justify-center overflow-visible md:flex">
      <div className="absolute top-[62px] flex w-[300px] items-center justify-center">
        <div className="absolute left-[-38px] right-[-38px] top-[104px] h-px bg-gradient-to-r from-[#f5c842]/0 via-[#f5c842]/95 to-[#f5c842]/0 shadow-[0_0_18px_rgba(245,200,66,0.85)]" />

        <div className="relative z-10 rotate-[-7deg]">
          <div className="absolute left-1/2 top-[-25px] z-20 flex -translate-x-1/2 items-center justify-center">
            <div className="relative h-[45px] w-[78px]">
              {Array.from({ length: 22 }).map((_, index) => {
                const beads = [
                  { left: 10, top: 34 },
                  { left: 9, top: 28 },
                  { left: 10, top: 22 },
                  { left: 13, top: 16 },
                  { left: 17, top: 11 },
                  { left: 22, top: 7 },
                  { left: 28, top: 4 },
                  { left: 35, top: 3 },
                  { left: 42, top: 4 },
                  { left: 49, top: 7 },
                  { left: 55, top: 11 },
                  { left: 60, top: 16 },
                  { left: 64, top: 22 },
                  { left: 66, top: 29 },
                  { left: 64, top: 35 },
                  { left: 59, top: 39 },
                  { left: 52, top: 41 },
                  { left: 45, top: 42 },
                  { left: 38, top: 42 },
                  { left: 31, top: 41 },
                  { left: 24, top: 39 },
                  { left: 17, top: 36 },
                ];

                return (
                  <span
                    key={index}
                    className="absolute h-[5px] w-[5px] rounded-full bg-[#d7b85a] shadow-[0_0_7px_rgba(245,200,66,0.75)]"
                    style={{
                      left: `${beads[index].left}px`,
                      top: `${beads[index].top}px`,
                    }}
                  />
                );
              })}
            </div>
          </div>

          <div className="pointer-events-none absolute inset-0 rounded-[18px] bg-black blur-[2px]" />
          <div className="pointer-events-none absolute inset-0 rounded-[18px] bg-[#f5c842]/10 blur-[20px]" />

          <div className="relative w-[190px] overflow-hidden rounded-[18px] border border-white/15 bg-[#050505] px-4 pb-5 pt-7 shadow-[0_18px_42px_rgba(0,0,0,0.72),0_0_24px_rgba(245,200,66,0.14),inset_0_0_28px_rgba(255,255,255,0.035)]">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_22%_18%,rgba(255,255,255,0.13),transparent_2px),radial-gradient(circle_at_72%_38%,rgba(255,255,255,0.08),transparent_1.5px),radial-gradient(circle_at_36%_76%,rgba(255,255,255,0.08),transparent_1.5px),radial-gradient(circle_at_84%_82%,rgba(255,255,255,0.1),transparent_1.5px)] opacity-60" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.06] via-transparent to-black/70" />

            <div className="absolute left-1/2 top-3 h-[21px] w-[21px] -translate-x-1/2 rounded-full border border-[#d8b657]/90 bg-black shadow-[0_0_10px_rgba(245,200,66,0.35),inset_0_0_10px_rgba(245,200,66,0.12)]" />
            <div className="absolute left-1/2 top-[18px] h-[7px] w-[7px] -translate-x-1/2 rounded-full bg-[#d8b657]" />

            <p className="relative mt-8 text-center text-[8px] font-black uppercase tracking-[0.34em] text-[#d8b657]">
              Matched
            </p>

            <div className="relative mt-5">
              <p
                className={`${travisFont.className} text-center text-[25px] leading-[1.02] text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.2)]`}
              >
                SAME FIT DNA
              </p>

              <p
                className={`${travisFont.className} mt-4 text-center text-[24px] leading-[1.05] text-[#f5c842] drop-shadow-[0_0_9px_rgba(245,200,66,0.48)]`}
              >
                DIFFERENT
              </p>

              <p
                className={`${travisFont.className} mt-1 text-center text-[24px] leading-[1.05] text-[#f5c842] drop-shadow-[0_0_9px_rgba(245,200,66,0.48)]`}
              >
                TICKET
              </p>
            </div>

            <div className="relative mx-auto mt-5 w-[128px] px-1 py-1">
              <div className="flex h-[28px] items-end justify-between gap-[1px]">
                <span className="h-6 w-[2px] bg-white/90" />
                <span className="h-7 w-[1px] bg-white/70" />
                <span className="h-5 w-[3px] bg-white/85" />
                <span className="h-7 w-[1px] bg-white/75" />
                <span className="h-6 w-[2px] bg-white/90" />
                <span className="h-7 w-[3px] bg-white/80" />
                <span className="h-4 w-[1px] bg-white/65" />
                <span className="h-7 w-[2px] bg-white/90" />
                <span className="h-5 w-[1px] bg-white/70" />
                <span className="h-7 w-[3px] bg-white/85" />
                <span className="h-4 w-[2px] bg-white/70" />
                <span className="h-7 w-[1px] bg-white/80" />
                <span className="h-6 w-[3px] bg-white/75" />
                <span className="h-7 w-[1px] bg-white/90" />
                <span className="h-5 w-[2px] bg-white/65" />
                <span className="h-7 w-[3px] bg-white/85" />
                <span className="h-6 w-[1px] bg-white/70" />
                <span className="h-7 w-[2px] bg-white/95" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BuildCard({ type, title, tag, total, items }: any) {
  const isBudget = type === "budget";

  return (
    <div
      className={`min-h-[300px] rounded-[18px] border bg-black/90 p-5 backdrop-blur-xl ${
        isBudget ? "border-emerald-400/65" : "border-[#f5c842]/75"
      }`}
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="flex gap-2 text-xl font-black uppercase">
          <span className={isBudget ? "text-emerald-400" : "text-[#f5c842]"}>
            {title.split(" ")[0]}
          </span>
          <span>Build</span>
        </div>

        <span
          className={`rounded-full border px-4 py-2 text-xs font-black ${
            isBudget
              ? "border-emerald-400/75 text-emerald-400"
              : "border-[#f5c842]/75 text-[#f5c842]"
          }`}
        >
          {tag}
        </span>
      </div>

      <p className="mb-4 text-[42px] font-black leading-none">{total}</p>

      <div className="space-y-3">
        {items.map((item: any) => (
          <div
            key={item.name}
            className="flex items-center gap-3 rounded-xl border border-white/12 bg-white/[0.04] p-3"
          >
            <div className="h-[64px] w-[64px] overflow-hidden rounded-lg bg-white/10">
              <AppImage
                src={item.img}
                alt={item.name}
                width={90}
                height={90}
                className="h-full w-full object-cover"
              />
            </div>

            <div className="flex-1">
              <p
                className={`text-xs font-black uppercase ${
                  isBudget ? "text-emerald-400" : "text-[#f5c842]"
                }`}
              >
                {item.category}
              </p>

              <p className="text-sm font-bold">{item.name}</p>
              <p className="text-xs text-white/45">{item.brand}</p>
            </div>

            <p className="text-lg font-bold">{item.price}</p>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-4">
        <p className="text-lg font-black uppercase text-white/45">
          Total Outfit
        </p>

        <p
          className={`text-4xl font-black ${
            isBudget ? "text-emerald-400" : "text-[#f5c842]"
          }`}
        >
          {total}
        </p>
      </div>
    </div>
  );
}

function BottomBar() {
  return (
    <div className="relative z-40 mt-12 flex justify-center">
      <div className="relative text-center">
        <p
          className={`${travisFont.className} text-[22px] leading-[0.88] text-[#f5c842] drop-shadow-[0_0_11px_rgba(245,200,66,0.58)]`}
        >
          MATCHED STYLE
        </p>

        <p
          className={`${infoFont.className} mt-3 text-[13px] font-semibold leading-none tracking-[0.01em] text-white/82`}
        >
          Same aesthetic. Different price points.
        </p>
      </div>
    </div>
  );
}